import { useRef, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { Modal, ModalContent } from '@heroui/react';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useModalSize } from '@/hooks/useModalSize';
import { maxModalHeight, maxModalWidth } from '@/utils/modal-size';
import { setGlobalCursor } from '@/utils/pointer-drag';

interface ResizableModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Distinguishes this modal's remembered size from every other modal's. */
  storageKey: string;
  classNames?: { base?: string; header?: string; footer?: string };
  /** ModalHeader / ModalBody / ModalFooter. This component supplies ModalContent. */
  children: ReactNode;
}

/**
 * A HeroUI Modal that opens wide and can be dragged to any size from its
 * bottom-right corner.
 *
 * The size reaches the dialog as an inline `style` rather than a class.
 * ModalContent spreads its own props onto the base element through
 * getDialogProps, and inline styles beat the `max-w-*` and `max-h-*` the size
 * and scrollBehavior variants put there — which classes alone would not.
 * Runtime pixel values could not be Tailwind classes in any case; the JIT only
 * emits arbitrary values it can see at build time.
 *
 * Desktop only. Below 1024px this renders exactly what these modals rendered
 * before the feature existed: a plain 4xl modal with no grip. The 1180px width
 * floor exceeds a tablet viewport, and components/project/mobile/ handles small
 * screens separately.
 */
export default function ResizableModal({
  isOpen,
  onClose,
  storageKey,
  classNames,
  children,
}: ResizableModalProps) {
  const { isDesktop } = useBreakpoint();
  const { size, viewport, resize, commit } = useModalSize(storageKey, isOpen, isDesktop);
  const gripRef = useRef<HTMLButtonElement>(null);
  const dragRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);

  const onPointerDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
    // The base element is the grip's own parent: ModalContent renders its
    // children straight into it. Reading the height off the DOM is what lets
    // the first drag start from the true rendered height while `size.height` is
    // still null (sized to content). It also avoids depending on ModalContent
    // forwarding a ref, which its typings do not promise.
    const base = gripRef.current?.parentElement;
    if (!base) return;
    const rect = base.getBoundingClientRect();
    dragRef.current = { x: e.clientX, y: e.clientY, width: rect.width, height: rect.height };
    e.currentTarget.setPointerCapture(e.pointerId);
    setGlobalCursor('nwse-resize');
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const start = dragRef.current;
    if (!start) return;
    resize({
      width: start.width + (e.clientX - start.x),
      height: start.height + (e.clientY - start.y),
    });
  };

  const endDrag = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
    setGlobalCursor(null);
    commit();
  };

  const sized = isDesktop && size !== null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="4xl"
      scrollBehavior="inside"
      classNames={{
        // `relative` anchors the absolutely positioned grip. Harmless if the
        // HeroUI theme already sets it — tailwind-merge dedupes.
        base: ['relative', classNames?.base].filter(Boolean).join(' '),
        header: classNames?.header,
        footer: classNames?.footer,
      }}
    >
      <ModalContent
        style={
          sized
            ? {
                width: size.width,
                maxWidth: maxModalWidth(viewport),
                // undefined, not null: height stays content-driven until dragged.
                height: size.height ?? undefined,
                maxHeight: maxModalHeight(viewport),
              }
            : undefined
        }
      >
        {children}
        {sized && (
          <button
            ref={gripRef}
            type="button"
            aria-label="Resize dialog"
            title="Drag to resize"
            className="absolute bottom-0 right-0 z-20 p-1 touch-none"
            style={{ cursor: 'nwse-resize' }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
              <path
                d="M11 1 L1 11 M11 5 L5 11 M11 9 L9 11"
                stroke="var(--text-muted)"
                strokeWidth="1.5"
                fill="none"
              />
            </svg>
          </button>
        )}
      </ModalContent>
    </Modal>
  );
}
